import React, { useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Heading,
  List,
  ListItem,
  Stack,
  Text,
} from '@chakra-ui/react'
import { httpsCallable } from 'firebase/functions'
import { useNavigate, useParams } from 'react-router-dom'
import useReportMonthGuard from '../../../hooks/useReportMonthGuard'
import useStore from '../../../store/useStore'
import { getIncompleteStepNames } from '../../../utils/reportCompletion'
import { getEffectiveServices } from '../../../utils/firebaseServiceContext'

export default function ReportCompletePage() {
  const isValidated = useReportMonthGuard()
  const { month = '' } = useParams<{ month: string }>()
  const navigate = useNavigate()
  const report = useStore((s) => s.reports)[month]
  const isImpersonating = useStore(
    (state) => state.identityStatus === 'impersonating',
  )

  const [dialogState, setDialogState] = useState<
    'idle' | 'incomplete' | 'complete'
  >('idle')
  const cancelRef = useRef<HTMLButtonElement>(null)

  const incompleteSteps = getIncompleteStepNames(report)

  const handleVerify = () => {
    if (incompleteSteps.length === 0 && !isImpersonating) {
      httpsCallable(
        getEffectiveServices().functions,
        'sendCheckInCompleteEmailCallable',
      )({ month }).catch(console.error)
    }
    setDialogState(incompleteSteps.length > 0 ? 'incomplete' : 'complete')
  }

  const handleOk = () => {
    setDialogState('idle')
    navigate('/')
  }

  if (!isValidated) {
    return null
  }

  return (
    <Stack spacing={6}>
      <Stack>
        <Heading>Excellent job!</Heading>
        <Text>You have taken one more step to healing.</Text>
      </Stack>
      <Button
        alignSelf='flex-start'
        size='lg'
        onClick={handleVerify}
        title={
          isImpersonating
            ? 'Completion email is suppressed while acting as a user'
            : undefined
        }
      >
        Verify I have completed Monthly Check-In
      </Button>
      <AlertDialog
        isOpen={dialogState === 'incomplete'}
        leastDestructiveRef={cancelRef}
        onClose={() => setDialogState('idle')}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Steps Not Yet Complete</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={2}>The following steps are not yet complete:</Text>
              <List spacing={1}>
                {incompleteSteps.map((step) => (
                  <ListItem key={step}>{step}</ListItem>
                ))}
              </List>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleOk}>
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        isOpen={dialogState === 'complete'}
        leastDestructiveRef={cancelRef}
        onClose={() => setDialogState('idle')}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Monthly Check-In Complete</AlertDialogHeader>
            <AlertDialogBody>
              Thank you! Your participation is helping find solutions for all
              those with ALS.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleOk}>
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Stack>
  )
}
